import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import {
  Globe, Heart, FileText, Users, CheckCircle, Clock, ArrowRight,
  Baby, Shield, Search, BookOpen, Phone, AlertCircle, Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ROUTE_PATHS } from "@/lib/index";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.12 } },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const etapes = [
  {
    numero: 1,
    titre: "Demande et premier contact",
    description:
      "Prise de contact avec le centre, explication de votre projet d'adoption et évaluation de l'admissibilité au service.",
    icon: Phone,
  },
  {
    numero: 2,
    titre: "Entrevues individuelles",
    description:
      "Rencontres approfondies avec chaque parent adoptif pour évaluer les motivations, l'histoire de vie, la stabilité émotionnelle et les capacités parentales.",
    icon: Users,
  },
  {
    numero: 3,
    titre: "Évaluation du couple",
    description:
      "Rencontre commune pour évaluer la dynamique conjugale, la communication, les valeurs partagées et le projet de vie familial.",
    icon: Heart,
  },
  {
    numero: 4,
    titre: "Visites à domicile",
    description:
      "Évaluation de l'environnement familial, de la stabilité du milieu de vie et des conditions d'accueil pour l'enfant.",
    icon: Shield,
  },
  {
    numero: 5,
    titre: "Vérifications et références",
    description:
      "Consultation de sources collatérales (références personnelles, professionnelles), vérification des antécédents judiciaires et des services sociaux.",
    icon: Search,
  },
  {
    numero: 6,
    titre: "Rédaction du rapport",
    description:
      "Rédaction d'un rapport psychosocial complet et détaillé, conforme aux exigences du Secrétariat à l'adoption internationale (SAI) du Québec.",
    icon: FileText,
  },
];

const criteresCouvertures = [
  "Motivation et projet d'adoption",
  "Histoire personnelle et familiale",
  "Stabilité émotionnelle et psychologique",
  "Capacités parentales et éducatives",
  "Dynamique conjugale et communication",
  "Réseau de soutien social et familial",
  "Conditions matérielles et environnement de vie",
  "Ouverture à la différence culturelle et identitaire",
  "Capacité d'attachement et sensibilité aux besoins de l'enfant",
  "Gestion des défis liés à l'adoption internationale",
  "Connaissance des enjeux particuliers des enfants adoptés",
  "Plan d'intégration et accompagnement post-adoption",
];

const paysHabituels = [
  { pays: "Chine", region: "Asie" },
  { pays: "Haïti", region: "Amérique" },
  { pays: "Colombie", region: "Amérique" },
  { pays: "Éthiopie", region: "Afrique" },
  { pays: "Vietnam", region: "Asie" },
  { pays: "Philippines", region: "Asie" },
  { pays: "Ukraine", region: "Europe" },
  { pays: "Inde", region: "Asie" },
];

const avantages = [
  {
    titre: "Expertise reconnue",
    description:
      "Rapports conformes aux exigences du Secrétariat à l'adoption internationale (SAI) du Québec et des pays d'origine.",
    icon: Star,
  },
  {
    titre: "Approche bienveillante",
    description:
      "Un processus respectueux de votre projet de vie, qui reconnaît la profondeur et la légitimité de votre désir d'adopter.",
    icon: Heart,
  },
  {
    titre: "Rigueur professionnelle",
    description:
      "Évaluation objective, complète et documentée répondant aux normes professionnelles les plus élevées de la travailleuse sociale.",
    icon: Shield,
  },
  {
    titre: "Accompagnement humain",
    description:
      "Soutien tout au long du processus d'évaluation, avec une communication claire et transparente à chaque étape.",
    icon: Users,
  },
];

export default function AdoptionInternationale() {
  return (
    <div>
      <SEO
        title="Évaluation psychosociale adoption internationale — Québec"
        description="Évaluation psychosociale pour l'adoption internationale au Québec. Service spécialisé pour les candidats à l'adoption, conforme aux exigences du Secrétariat à l'adoption internationale. Gatineau, Outaouais."
        keywords="évaluation psychosociale adoption internationale Québec, adoption enfant étranger évaluation, Secrétariat adoption internationale, candidats adoption Gatineau, rapport adoption internationale"
        canonical="/adoption-internationale"
      />
      {/* Hero */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem} className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Globe className="w-10 h-10 text-primary" />
              </div>
            </motion.div>
            <motion.span
              variants={staggerItem}
              className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-2 rounded-full mb-6"
            >
              Service spécialisé
            </motion.span>
            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground"
              variants={staggerItem}
            >
              Évaluation psychosociale pour l'adoption internationale
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground mb-10 max-w-3xl mx-auto leading-relaxed"
              variants={staggerItem}
            >
              Un accompagnement professionnel et bienveillant pour votre projet d'adoption d'un enfant à l'international. Rapports conformes aux exigences du Secrétariat à l'adoption internationale (SAI) du Québec.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={staggerItem}
            >
              <Button asChild size="lg" className="text-lg px-8 py-6">
                <a href="https://expertisefamiliale.janeapp.com" target="_blank" rel="noopener noreferrer">
                  Prendre rendez-vous
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6">
                <Link to={ROUTE_PATHS.CONTACT}>Nous contacter</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Qu'est-ce que l'évaluation psychosociale */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="grid md:grid-cols-2 gap-16 items-center"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem}>
                <span className="inline-block bg-secondary/10 text-secondary text-sm font-semibold px-4 py-2 rounded-full mb-4">
                  À propos du service
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Qu'est-ce que l'évaluation psychosociale pour adoption ?
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  L'évaluation psychosociale pour l'adoption internationale est une étape obligatoire au Québec. Elle est réalisée par une travailleuse sociale membre de l'<strong>Ordre des travailleurs sociaux et des thérapeutes conjugaux et familiaux du Québec (OTSTCFQ)</strong>.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Ce rapport est exigé par le <strong>Secrétariat à l'adoption internationale (SAI)</strong> du gouvernement du Québec, ainsi que par les autorités du pays d'origine de l'enfant. Il vise à évaluer les capacités et aptitudes des futurs parents adoptifs à accueillir un enfant dans leur famille.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Notre approche conjugue rigueur professionnelle et sensibilité humaine, reconnaissant la profondeur et la légitimité de votre projet d'adoption.
                </p>
              </motion.div>

              <motion.div variants={staggerItem} className="space-y-4">
                <div className="bg-primary/5 rounded-2xl p-6 border border-primary/10">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                      <Baby className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Pour qui ?</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Toute personne seule ou tout couple (marié, uni civilement ou en union de fait) résidant au Québec et souhaitant adopter un enfant à l'étranger.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary/5 rounded-2xl p-6 border border-secondary/10">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0 mt-1">
                      <Clock className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Durée du processus</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Le processus d'évaluation s'étend généralement sur <strong>4 à 6 semaines</strong>, selon la disponibilité des parties et la complexité du dossier.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-accent/5 rounded-2xl p-6 border border-accent/10">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0 mt-1">
                      <FileText className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Le rapport final</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        Un rapport psychosocial complet, signé par une travailleuse sociale accréditée, conforme aux exigences du SAI et des pays partenaires.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Processus étape par étape */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-2 rounded-full mb-4">
              Notre processus
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Les étapes de l'évaluation
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Un processus structuré, transparent et respectueux de votre projet familial.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              className="space-y-6"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {etapes.map((etape) => {
                const Icon = etape.icon;
                return (
                  <motion.div key={etape.numero} variants={staggerItem}>
                    <Card className="border border-border hover:shadow-md transition-shadow duration-200">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-6">
                          <div className="flex flex-col items-center gap-2 flex-shrink-0">
                            <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                              {etape.numero}
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                          </div>
                          <div className="pt-1">
                            <h3 className="text-xl font-semibold mb-2 text-foreground">
                              {etape.titre}
                            </h3>
                            <p className="text-muted-foreground leading-relaxed">
                              {etape.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Critères évalués */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-5xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div className="text-center mb-16" variants={staggerItem}>
              <span className="inline-block bg-secondary/10 text-secondary text-sm font-semibold px-4 py-2 rounded-full mb-4">
                Contenu de l'évaluation
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ce que couvre le rapport psychosocial
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Une évaluation globale et approfondie pour dresser un portrait complet de votre situation et de votre projet d'adoption.
              </p>
            </motion.div>

            <motion.div
              className="grid sm:grid-cols-2 md:grid-cols-3 gap-4"
              variants={staggerContainer}
            >
              {criteresCouvertures.map((critere, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="flex items-start gap-3 bg-card p-4 rounded-xl border border-border"
                >
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground leading-relaxed">{critere}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pays habituels */}
      <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div className="text-center mb-12" variants={staggerItem}>
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-2 rounded-full mb-4">
                Portée internationale
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Pays d'adoption habituellement desservis
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Nous préparons des rapports conformes aux exigences des différents pays partenaires du Québec en matière d'adoption internationale.
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8"
              variants={staggerContainer}
            >
              {paysHabituels.map((item, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="bg-card p-4 rounded-xl border border-border text-center hover:shadow-md transition-shadow"
                >
                  <Globe className="w-6 h-6 text-primary mx-auto mb-2" />
                  <p className="font-semibold text-foreground text-sm">{item.pays}</p>
                  <p className="text-xs text-muted-foreground mt-1">{item.region}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              variants={staggerItem}
              className="bg-card rounded-2xl border border-border p-6 flex items-start gap-4"
            >
              <AlertCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-muted-foreground text-sm leading-relaxed">
                <strong className="text-foreground">Note importante :</strong> Les pays accessibles à l'adoption internationale varient selon les ententes bilatérales du Québec. Nous vous conseillons de vérifier auprès du <strong>Secrétariat à l'adoption internationale (SAI)</strong> la liste des pays actuellement accessibles. Contactez-nous pour toute question sur votre projet spécifique.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pourquoi nous choisir */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-secondary/10 text-secondary text-sm font-semibold px-4 py-2 rounded-full mb-4">
              Nos engagements
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Pourquoi nous confier votre évaluation ?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Un service spécialisé qui conjugue expertise professionnelle et accompagnement humain.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {avantages.map((avantage, index) => {
              const Icon = avantage.icon;
              return (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="h-full border border-border hover:shadow-lg transition-shadow duration-200 text-center">
                    <CardContent className="p-8">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold mb-3 text-foreground">{avantage.titre}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{avantage.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Ressources utiles */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div className="text-center mb-12" variants={staggerItem}>
              <h2 className="text-3xl font-bold mb-4">Ressources utiles</h2>
              <p className="text-muted-foreground">
                Organismes officiels pour votre démarche d'adoption internationale au Québec.
              </p>
            </motion.div>
            <motion.div className="grid sm:grid-cols-2 gap-6" variants={staggerContainer}>
              <motion.div variants={staggerItem}>
                <Card className="border border-border hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Secrétariat à l'adoption internationale (SAI)</h3>
                      <p className="text-muted-foreground text-sm mb-3">Organisme gouvernemental qui encadre l'adoption internationale au Québec.</p>
                      <a
                        href="https://www.adoption.gouv.qc.ca"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-sm font-medium hover:underline inline-flex items-center gap-1"
                      >
                        adoption.gouv.qc.ca <ArrowRight className="w-3 h-3" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div variants={staggerItem}>
                <Card className="border border-border hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">OTSTCFQ</h3>
                      <p className="text-muted-foreground text-sm mb-3">Ordre des travailleurs sociaux et thérapeutes conjugaux et familiaux du Québec.</p>
                      <a
                        href="https://www.otstcfq.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-sm font-medium hover:underline inline-flex items-center gap-1"
                      >
                        otstcfq.org <ArrowRight className="w-3 h-3" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-24 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Baby className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à commencer votre démarche ?
            </h2>
            <p className="text-muted-foreground text-lg mb-10 leading-relaxed">
              Contactez-nous dès aujourd'hui pour discuter de votre projet d'adoption internationale. Nous vous accompagnerons à chaque étape de cette belle aventure familiale avec professionnalisme et bienveillance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8 py-6">
                <a href="https://expertisefamiliale.janeapp.com" target="_blank" rel="noopener noreferrer">
                  Prendre rendez-vous
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6">
                <Link to={ROUTE_PATHS.CONTACT}>Nous écrire</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
